// SEO-geoptimaliseerde landingspagina-content voor de QuickNav categorieën.
// Voldoet aan de richtlijnen van Google Merchant Center en Google Ads:
// duidelijke productinformatie, transparante prijzen via de webshop,
// eerlijke claims, contactgegevens en garantie via de Footer.

export type CategoryInfoFAQ = { q: string; a: string };

export type CategoryInfo = {
  slug:
    | "groepenkasten"
    | "verlichting"
    | "beveiliging"
    | "laadpalen"
    | "thuisbatterij"
    | "netwerken";
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  shopTo?: "/c/$category" | "/c/$category/$subcategory";
  shopParams?: { category: string; subcategory?: string };
  shopLabel?: string;
  sections: { heading: string; body: string }[];
  benefits: string[];
  applications: string[];
  faqs: CategoryInfoFAQ[];
};

export const CATEGORY_INFO: Record<string, CategoryInfo> = {
  verlichting: {
    slug: "verlichting",
    title: "Verlichting",
    h1: "LED Verlichting: energiezuinig en zakelijk leverbaar",
    metaTitle: "LED verlichting kopen — GU10, E27, TL & werklampen | Elvion Group Nederland",
    metaDescription:
      "Energiezuinige LED-verlichting voor woning en bedrijf: GU10, E27, TL-buis en werklampen van Philips en Osram. Snel leverbaar via Elvion Group Nederland.",
    intro:
      "LED verlichting verbruikt tot 85% minder energie dan traditionele gloei- en halogeenlampen en gaat vele jaren mee. Wij leveren bewezen kwaliteit van merken als Philips en Osram, met heldere specificaties zoals lumen, kleurtemperatuur en levensduur.",
    shopTo: "/c/$category",
    shopParams: { category: "verlichting" },
    shopLabel: "Bekijk alle LED verlichting",
    sections: [
      {
        heading: "Welke kleurtemperatuur kies ik?",
        body:
          "Warm wit (2700K–3000K) past in woonkamers en slaapkamers. Neutraal wit (4000K) is ideaal voor keuken, kantoor en werkplaats. Daglicht wit (5000K–6500K) gebruik je voor garages, magazijnen en taakverlichting.",
      },
      {
        heading: "Dimbaar of niet-dimbaar?",
        body:
          "Niet alle LED-lampen zijn dimbaar. Combineer een dimbare LED altijd met een fase-aansnijdings- of LED-dimmer. Onze productpagina's vermelden expliciet of een lamp dimbaar is.",
      },
    ],
    benefits: [
      "Tot 85% energiebesparing t.o.v. gloeilampen",
      "Lange levensduur (15.000–50.000 branduren)",
      "A-merken: Philips, Osram",
      "Volledige specificaties: lumen, K-waarde, dimbaarheid",
    ],
    applications: [
      "Woonkamer, keuken en slaapkamer",
      "Kantoor en winkelruimte",
      "Werkplaats, garage en magazijn",
      "Buitenverlichting en werkterrein",
    ],
    faqs: [
      {
        q: "Wat betekent lumen (lm)?",
        a: "Lumen geeft de daadwerkelijke lichtopbrengst aan. Een traditionele 60W gloeilamp geeft circa 800 lumen — vergelijkbaar met een 9W LED. Kijk dus naar lumen, niet naar wattage, om de juiste vervanging te kiezen.",
      },
      {
        q: "Wat is het verschil tussen warm wit, neutraal wit en daglicht wit?",
        a: "Warm wit (2700K–3000K) geeft een sfeervol, geelachtig licht en past goed in woonkamers en slaapkamers. Neutraal wit (3500K–4500K) is helderder en geschikt voor keukens, kantoren en werkruimtes. Daglicht wit (5000K–6500K) lijkt op daglicht en wordt vaak gebruikt in garages, werkplaatsen en commerciële ruimtes waar optimale kleurweergave belangrijk is.",
      },
      {
        q: "Kan ik een LED-lamp dimmen met mijn bestaande dimmer?",
        a: "Niet alle LED-lampen zijn dimbaar, en niet elke dimmer werkt met LED. Bestaande fase-aansnijdingsdimmers kunnen problemen geven zoals flikkeren, zoemen of niet volledig dimmen. Kies altijd een LED-lamp die expliciet dimbaar is en combineer deze bij voorkeur met een LED-compatibele dimmer of fase-afsnijdingsdimmer voor het beste resultaat.",
      },
      {
        q: "Welke fittingen zijn er en welke heb ik nodig?",
        a: "De meest voorkomende fittingen zijn E27 (grote fitting, vaak in plafondlampen), E14 (kleine fitting, in kroonluchters en bedlampen), GU10 (spotfitting, veel gebruikt in inbouwspots), MR16/GU5.3 (12V spots, vaak met transformator) en G9 (kleine pinfitting, compact). Controleer je huidige lamp of armatuur om de juiste fitting te bepalen.",
      },
      {
        q: "Mag ik LED spots in een vochtige ruimte zoals de badkamer gebruiken?",
        a: "Ja, mits je kiest voor een lamp en armatuur met de juiste IP-waarde. Voor de badkamer (zones 2 en 3) adviseren wij minimaal IP44. Voor direct boven het bad of in de douche (zone 0 en 1) is IP65 verplicht. Buitenverlichting heeft minimaal IP44 nodig, maar voor blootgestelde armaturen raden wij IP65 aan.",
      },
      {
        q: "Hoe lang gaan LED-lampen echt mee?",
        a: "LED-lampen gaan doorgaans 15.000 tot 50.000 branduren mee. Bij gemiddeld gebruik van 3 uur per dag is dat 14 tot 45 jaar. De levensduur wordt echter beïnvloed door warmte-opbouw ( slechte afvoer in gesloten armaturen), spanningspieken en het aantal aan- en uitschakelcycli.",
      },
      {
        q: "Is LED verlichting flikkervrij?",
        a: "Hoogwaardige LED-lampen van A-merken zoals Philips en Osram zijn vrijwel flikkervrij (<5% flicker). Goedkopere alternatieven kunnen echter merkbaar knipperen, wat hoofdpijn en vermoeide ogen kan veroorzaken. Kijk naar een laag flicker-percentage en een hoge flicker-frequentie (>100 Hz) bij professioneel gebruik.",
      },
      {
        q: "Wat is CRI en waarom is het belangrijk?",
        a: "CRI (Colour Rendering Index) geeft aan hoe natuurgetrouw kleuren worden weergegeven onder het licht. Een CRI van 80 is acceptabel voor algemeen gebruik, maar voor keukens, werkplaatsen en winkels raden wij CRI 90 of hoger aan. Voor professionele toepassingen zoals fotografie of horeca is CRI 95+ wenselijk.",
      },
      {
        q: "Kan ik LED strips zelf installeren?",
        a: "Ja, LED strips zijn relatief eenvoudig zelf te installeren. Let op: 12V of 24V strips hebben een geschikte transformator/driver nodig. Meet de totale lengte en het vermogen per meter om de juiste driver te kiezen. Gebruik profielen voor betere warmteafvoer en een strakkere uitstraling. Bij twijfel adviseren wij een installateur in te schakelen.",
      },
      {
        q: "Hoe snel verdient LED verlichting zich terug?",
        a: "De terugverdientijd hangt af van je huidige verlichting, het aantal branduren en de stroomprijs. Bij vervanging van gloei- of halogeenlampen in ruimtes met veel verlichting (keuken, kantoor) verdient LED zich vaak binnen 1 tot 2 jaar terug door de 70–85% lagere energiekosten en minder vervanging.",
      },
      {
        q: "Hebben jullie garantie op LED-lampen?",
        a: "Ja. Op alle producten geldt de wettelijke garantie en op veel A-merk LED-lampen aanvullende fabrieksgarantie. Details staan op de productpagina.",
      },
      {
        q: "Is LED verlichting echt energiezuiniger?",
        a: "Ja, LED verlichting is tot 85% energiezuiniger dan traditionele gloei- en halogeenlampen. Door de hoge efficiëntie en lange levensduur (15.000 tot 50.000 branduren) bespaar je zowel op energiekosten als vervangingskosten.",
      },
    ],
  },

  beveiliging: {
    slug: "beveiliging",
    title: "Beveiliging",
    h1: "Beveiligingscamera's en intercoms voor woning en bedrijf",
    metaTitle: "IP-camera's, NVR & intercoms — Hikvision & Dahua | Elvion",
    metaDescription:
      "Professionele beveiliging: IP camera's, netwerkrecorders (NVR), intercomsystemen en montagebeugels van Hikvision en Dahua. Snel leverbaar.",
    intro:
      "Bescherm je woning of bedrijfspand met professionele beveiligingsoplossingen van Hikvision en Dahua. Wij leveren scherpe IP-camera's (4MP tot 8MP), netwerkrecorders, IP-intercoms en bijbehorende montagematerialen altijd inclusief volledige specificaties.",
    shopTo: "/c/$category",
    shopParams: { category: "beveiliging" },
    shopLabel: "Bekijk alle beveiligingsproducten",
    sections: [
      {
        heading: "Welk type camera past bij mijn situatie?",
        body:
          "Een dome-camera is vandalismebestendig en geschikt voor binnen. Een turret-camera levert helder beeld zonder reflectie en is geschikt voor buiten. Voor groot terrein gebruik je een PTZ-camera met optische zoom en automatische tracking.",
      },
      {
        heading: "AI-functies: AcuSense en WizSense",
        body:
          "Moderne camera's filteren met AcuSense (Hikvision) of WizSense (Dahua) loos alarm uit door alleen mensen en voertuigen te detecteren. Dit voorkomt meldingen door wapperende takken of langslopende dieren.",
      },
    ],
    benefits: [
      "Resoluties van 4MP tot 8MP",
      "IR-nachtzicht tot 100 meter (PTZ)",
      "AI-detectie: mens en voertuig",
      "IP66/IP67 weerbestendig",
    ],
    applications: [
      "Woninghuis en oprit",
      "Winkels en horeca",
      "Bedrijfspanden en magazijnen",
      "Bouwplaatsen en terreinen",
    ],
    faqs: [
      {
        q: "Mag ik buiten zomaar filmen?",
        a: "Camera's mogen alleen je eigen terrein in beeld brengen. Filmen van openbare weg of buurpercelen is in principe niet toegestaan onder de AVG. Plaats een duidelijk bordje 'cameratoezicht' en informeer huisgenoten, klanten of werknemers over de opnames.",
      },
      {
        q: "Heb ik een NVR nodig?",
        a: "Voor lokale opslag en 24/7 opname adviseren wij een Netwerk Video Recorder (NVR). Cloudopslag kan een aanvulling zijn, maar geen vervanging — bij een internetstoring blijft je NVR gewoon doordraaien.",
      },
      {
        q: "Wat is het verschil tussen 4MP, 6MP en 8MP (4K)?",
        a: "Hoe hoger de resolutie, hoe meer detail en hoe verder je kunt inzoomen zonder kwaliteitsverlies. 4MP is prima voor een oprit of kleine winkel, 6MP biedt extra detail, en 8MP (4K) is ideaal voor grote terreinen, parkeerplaatsen of situaties waar gezichten en kentekens herkenbaar moeten zijn.",
      },
      {
        q: "Hoe lang worden de beelden bewaard?",
        a: "Dat hangt af van de capaciteit van de harde schijf in je NVR, het aantal camera's, de resolutie en de framerate. Gemiddeld kun je rekenen op 2 tot 4 weken bij continue opname. Onder de AVG mag je beelden in de regel maximaal 4 weken bewaren, tenzij er een incident is vastgelegd.",
      },
      {
        q: "Werken Hikvision en Dahua samen in één systeem?",
        a: "Beide merken ondersteunen het ONVIF-protocol, waardoor je camera's van Hikvision en Dahua kunt mengen op één NVR. Voor de beste gebruikerservaring en volledige toegang tot AI-functies (AcuSense / WizSense) adviseren wij echter één merk per installatie.",
      },
      {
        q: "Heb ik internet nodig om de camera's te laten werken?",
        a: "Nee, opname op de NVR werkt volledig zelfstandig op je lokale netwerk. Internet heb je alleen nodig om beelden op afstand te bekijken via een app (Hik-Connect of DMSS) of voor pushmeldingen bij detectie.",
      },
      {
        q: "Kan ik een IP-intercom koppelen aan mijn camerasysteem?",
        a: "Ja. IP-intercoms van Hikvision en Dahua integreren naadloos met de bijbehorende NVR en app. Je ziet bezoekers live op je telefoon, kunt op afstand opendoen en gemiste oproepen worden opgenomen.",
      },
      {
        q: "Zijn de camera's bestand tegen regen en vorst?",
        a: "Onze buitencamera's hebben minimaal een IP66- of IP67-classificatie en zijn ontworpen voor het Nederlandse klimaat (-30°C tot +60°C). Ze zijn stof- en waterdicht en bestand tegen straalwater uit elke richting.",
      },
      {
        q: "Kunnen jullie de installatie ook verzorgen?",
        a: "Ja, onze monteurs verzorgen complete installaties: van bekabeling (UTP/PoE) en montage tot configuratie van de NVR, app-koppeling en uitleg. Neem contact op voor een vrijblijvende offerte op locatie.",
      },
    ],
  },

  laadpalen: {
    slug: "laadpalen",
    title: "Laadpalen",
    h1: "Laadpalen voor elektrische auto's,  thuis en zakelijk",
    metaTitle: "Laadpalen 11kW & 22kW — Alfen & Wallbox | Elvion Group Nederland",
    metaDescription:
      "Slimme laadpalen van Alfen en Wallbox met load balancing en MID-meter. 11kW en 22kW, geschikt voor thuis en zakelijk gebruik.",
    intro:
      "Een eigen laadpaal laadt je elektrische auto sneller, goedkoper en veiliger dan een stopcontact. Wij leveren slimme laadpalen van Alfen en Wallbox met dynamische load balancing  zodat je groepenkast nooit overbelast raakt en MID-gekeurde meter voor zakelijke verrekening.",
    sections: [
      {
        heading: "11 kW of 22 kW?",
        body:
          "11 kW (3-fase, 16A) is voldoende voor de meeste thuissituaties en is bij vrijwel iedere netbeheerder toegestaan. 22 kW (3-fase, 32A) is geschikt voor zakelijk gebruik en snel opladen, maar vereist een zwaardere netaansluiting.",
      },
      {
        heading: "Load balancing en zonnestroom",
        body:
          "Met dynamic load balancing past je laadpaal het laadvermogen automatisch aan op het beschikbare vermogen van je pand. In combinatie met PV-overschot laadt je auto bij voorkeur op je eigen opgewekte zonnestroom.",
      },
    ],
    benefits: [
      "11 kW of 22 kW vermogen",
      "Dynamic load balancing",
      "MID-gekeurde kWh-meter",
      "App-bediening en OCPP backoffice",
    ],
    applications: [
      "Thuis, op de oprit of in de garage",
      "Bedrijfsparkeerplaats",
      "VvE en appartementencomplexen",
      "Leasewagenpark",
    ],
    faqs: [
      {
        q: "Moet ik mijn groepenkast aanpassen?",
        a: "Vrijwel altijd is een aparte 3-fase groep met aardlekautomaat type B verplicht. Onze installateurs adviseren en plaatsen indien gewenst een nieuwe groepenkast.",
      },
      {
        q: "Kan ik subsidie krijgen?",
        a: "Zakelijke laadpalen vallen vaak onder de MIA-regeling. Vraag je adviseur om actuele mogelijkheden — wij denken graag mee.",
      },
      {
        q: "Hoe snel laadt mijn auto op met 11 kW of 22 kW?",
        a: "Met 11 kW (3-fase, 16A) laad je circa 50–60 km per uur bij. Met 22 kW (3-fase, 32A) verdubbelt dit naar 100–120 km per uur. De exacte snelheid hangt af van het maximale laadvermogen van je auto.",
      },
      {
        q: "Wat is dynamic load balancing en heb ik dat nodig?",
        a: "Dynamic load balancing meet continu het verbruik van je pand en past het laadvermogen van de laadpaal automatisch aan. Zo voorkom je dat de hoofdzekering doorslaat als je oven, wasmachine en laadpaal tegelijk draaien. Voor vrijwel alle moderne installaties adviseren wij deze functie.",
      },
      {
        q: "Kan ik mijn laadpaal bedienen via een app?",
        a: "Ja. Laadpalen van Alfen en Wallbox worden geleverd met een eigen app waarmee je het laadproces start, stopt en plant. Daarnaast ondersteunen ze het OCPP-protocol voor koppeling met backoffice-systemen en laadpas-platformen.",
      },
      {
        q: "Wat is het verschil tussen een type 1 en type 2 kabel?",
        a: "Type 2 is de Europese standaard en wordt door vrijwel alle moderne elektrische auto's gebruikt. Type 1 komt voornamelijk voor bij oudere Aziatische en Amerikaanse modellen. Alle laadpalen die wij leveren zijn standaard voorzien van een type 2 aansluiting.",
      },
      {
        q: "Kan ik een laadpaal laten installeren zonder zonnepanelen?",
        a: "Absoluut. Een laadpaal werkt onafhankelijk van zonnepanelen. Wel adviseren we dynamic load balancing, zodat het laadvermogen automatisch aangepast wordt op het beschikbare vermogen van je huisaansluiting.",
      },
      {
        q: "Hoe lang duurt de installatie van een laadpaal?",
        a: "Een standaard installatie op de buitenmuur of in de garage duurt 3 tot 5 uur. Bij ingewikkeldere situaties — zoals een lange kabelroute of aanpassing van de groepenkast — kan dit oplopen tot een dagdeel. Wij plannen graag een vrijblijvende inspectie op locatie.",
      },
      {
        q: "Is een laadpaal veiliger dan laden via een stopcontact?",
        a: "Ja. Een stopcontact (soms grappend 'laden op een pannenkoek' genoemd) is niet ontworpen voor continu hoog vermogen en kan oververhit raken. Een laadpaal beschikt over ingebouwde beveiliging zoals aardlekbeveiliging type B, temperatuurbeveiliging en automatische communicatie met de auto voor optimaal laadvermogen.",
      },
      {
        q: "Kan ik mijn laadpaal later uitbreiden met zonnepanelen of thuisbatterij?",
        a: "Ja, de meeste slimme laadpalen zijn voorbereid op toekomstige uitbreiding. Bij een PV-overschot installatie kan de laadpaal automatisch overschotten zonnestroom gebruiken om je auto op te laden. Combineer je dit met een thuisbatterij, dan bepaalt een energiemanager de slimste verdeling van je stroom.",
      },
    ],
  },

  thuisbatterij: {
    slug: "thuisbatterij",
    title: "Thuisbatterij",
    h1: "Thuisbatterij: bespaar op je energierekening",
    metaTitle: "Thuisbatterij kopen — modulair vanaf 5kWh | Elvion Group Nederland",
    metaDescription:
      "Modulaire thuisbatterijen vanaf 5 kWh, uitbreidbaar tot 15 kWh. Sla je zonnestroom op en bespaar op je energierekening. Inclusief BMS.",
    intro:
      "Met een thuisbatterij sla je overdag opgewekte zonnestroom op om 's avonds en 's nachts te gebruiken. Door het afbouwen van de salderingsregeling wordt eigen verbruik steeds belangrijker. Wij leveren modulaire Huawei LUNA 2000 systemen vanaf 5 kWh, uitbreidbaar tot 15 kWh.",
    sections: [
      {
        heading: "Hoeveel kWh heb ik nodig?",
        body:
          "Een gemiddeld huishouden gebruikt 's avonds 5–8 kWh. Een batterij van 5–10 kWh dekt daarmee het grootste deel van je avondverbruik op eigen zonnestroom. Voor grotere woningen of een warmtepomp adviseren we 10–15 kWh.",
      },
      {
        heading: "Werkt een thuisbatterij ook bij stroomuitval?",
        body:
          "Sommige systemen (zoals de Huawei LUNA met backup-box) kunnen kritieke groepen voorzien van stroom bij netuitval. Vraag onze adviseur naar de mogelijkheden voor jouw installatie.",
      },
    ],
    benefits: [
      "Modulair: 5, 10 of 15 kWh",
      "Battery Management System (BMS)",
      "Hoge cyclische levensduur (LFP)",
      "Integratie met bestaande omvormer",
    ],
    applications: [
      "Woningen met zonnepanelen",
      "Verminderen terugleveren bij salderingsafbouw",
      "Backup voor kritieke groepen",
      "Combinatie met laadpaal en warmtepomp",
    ],
    faqs: [
      {
        q: "Is een thuisbatterij rendabel?",
        a: "De terugverdientijd hangt af van je verbruik, opwek en de afbouw van saldering. Zonder saldering verdien je circa € 0,08 tot € 0,12 per kWh terug via terugleveren. Eigen verbruik via een batterij bespaart je juist de volledige stroomprijs (circa € 0,35–€ 0,45 per kWh). Bij een gemiddeld huishouden met 3.500 kWh opwek en 4.000 kWh verbruik kan een 10 kWh batterij zich in 7 tot 10 jaar terugverdienen. Wij maken graag een persoonlijke berekening.",
      },
      {
        q: "Hoe lang gaat een thuisbatterij mee?",
        a: "LFP-batterijen (lithium-ijzerfosfaat) zoals de Huawei LUNA gaan doorgaans 6.000+ volledige laadcycli mee. Bij dagelijks één cyclus is dat vergelijkbaar met 15 tot 20 jaar gebruik. Na deze periode is de resterende capaciteit nog circa 80%, dus de batterij blijft bruikbaar maar levert iets minder vermogen.",
      },
      {
        q: "Hoeveel kWh capaciteit heb ik nodig?",
        a: "Een gemiddeld huishouden gebruikt 's avonds 5 tot 8 kWh. Een batterij van 5 tot 10 kWh dekt daarmee het grootste deel van je avondverbruik op eigen zonnestroom. Voor grotere woningen, een warmtepomp of een laadpaal adviseren we 10 tot 15 kWh. Je kunt modulair uitbreiden: begin met 5 kWh en voeg later modules toe.",
      },
      {
        q: "Werkt een thuisbatterij ook bij stroomuitval?",
        a: "Sommige systemen bieden een back-up functie via een aparte backup-box die kritieke groepen van stroom voorziet bij netuitval. Let op: niet elke omvormer en batterijcombinatie ondersteunt dit. De Huawei LUNA 2000 in combinatie met een geschikte omvormer biedt deze optie. Vraag onze adviseur naar de mogelijkheden voor jouw installatie.",
      },
      {
        q: "Kan ik een thuisbatterij later bijplaatsen op bestaande zonnepanelen?",
        a: "Ja, in de meeste gevallen kan een thuisbatterij retroactief worden aangesloten op een bestaande zonne-installatie. De batterij wordt tussen de zonnepanelen en de omvormer geplaatst (AC-gekoppeld) of direct op de DC-kant van de panelen (DC-gekoppeld). DC-gekoppeld is efficiënter maar vereist een hybride omvormer. Wij adviseren je graag over de beste oplossing.",
      },
      {
        q: "Wat is het verschil tussen AC- en DC-gekoppelde batterijen?",
        a: "Een DC-gekoppelde batterij wordt direct op de gelijkstroomzijde van de zonnepanelen aangesloten, via een hybride omvormer. Dit is efficiënter omdat er minder conversieverliezen optreden. Een AC-gekoppelde batterij wordt achter de bestaande omvormer op de wisselstroomzijde aangesloten. Dit is flexibeler omdat het werkt met elke omvormer, maar er gaat iets meer energie verloren door de extra conversiestap.",
      },
      {
        q: "Hoeveel kan ik besparen met een thuisbatterij na de salderingsafbouw?",
        a: "Na volledige afschaffing van de salderingsregeling (verwacht na 2031) verdien je circa € 0,08 tot € 0,12 per kWh aan teruggeleverde stroom. Door je eigen opgewekte stroom op te slaan en 's avonds te gebruiken, bespaar je de volledige energieprijs inclusief belastingen en transportkosten — circa € 0,35 tot € 0,45 per kWh. Een 10 kWh batterij die dagelijks volledig leegloopt, bespaart daarmee jaarlijks € 1.200 tot € 1.600.",
      },
      {
        q: "Kan ik een thuisbatterij zelf installeren?",
        a: "Nee, een thuisbatterij valt onder de Woninginstallatie en mag alleen worden aangesloten door een erkende installateur. De installatie omvat het mechanisch monteren van de modules, elektrisch aansluiten op de omvormer of meterkast, configureren van het Battery Management System (BMS) en eventueel een keuring. Onze installateurs regelen dit volledig voor je.",
      },
      {
        q: "Wat is LFP en waarom is dat beter dan andere lithium-technologieën?",
        a: "LFP staat voor lithium-ijzerfosfaat. Dit batterijtype is thermisch stabieler, gaat langer mee (meer cycli) en is veiliger dan NMC (nikkel-mangaan-kobalt) batterijen. LFP-batterijen zijn minder gevoelig voor oververhitting en hebben een langere levensduur. Het nadeel is een iets lagere energiedichtheid, maar voor stationaire thuisopslag is dat irrelevant.",
      },
      {
        q: "Kan ik meerdere thuisbatterijen combineren?",
        a: "Ja, veel systemen zijn modulair uitbreidbaar. De Huawei LUNA 2000 kan bijvoorbeeld van 5 kWh worden uitgebreid tot 10 of 15 kWh door extra batterijmodules toe te voegen. De modules communiceren via een interne bus en worden als één systeem aangestuurd door het BMS. Zo kun je stapsgewijs investeren naarmate je verbruik groeit.",
      },
      {
        q: "Wat doet een Battery Management System (BMS)?",
        a: "Het BMS bewaakt en regelt de laad- en ontlaadprocessen van elke cel in de batterij. Het voorkomt overladen, te diep ontladen, oververhitting en kortsluiting. Daarnaast balanceert het de cellen zodat ze allemaal gelijkmatig worden gebruikt, wat de levensduur verlengt. Een goed BMS is essentieel voor de veiligheid en prestaties van je thuisbatterij.",
      },
      {
        q: "Hoe groot is een thuisbatterij en waar monteer je die?",
        a: "Een standaard 5 kWh module is ongeveer 60 × 15 × 55 cm (b × d × h) en weegt 45 tot 60 kg. Grotere systemen bestaan uit meerdere modules die verticaal of horizontaal gestapeld kunnen worden. Ideale montagelocaties zijn een garage, kelder, technische ruimte of buitenmuur (mits IP65 en vorstbestendig). Wij adviseren over de beste locatie in jouw situatie.",
      },
    ],
  },

  netwerken: {
    slug: "netwerken",
    title: "Netwerken",
    h1: "Netwerken: bekabeling, routers en wifi voor pro's",
    metaTitle: "Netwerkproducten — CAT6a, UniFi, wifi & powerline | Elvion Group Nederland",
    metaDescription:
      "Professionele netwerkproducten: CAT6a kabel, keystone connectoren, UniFi routers, wifi-versterkers en powerline bridges. Snel leverbaar.",
    intro:
      "Een stabiel netwerk is de basis voor camera's, smart home, VoIP en cloudwerken. Wij leveren professionele datakabel (CAT6a, U/UTP), connectoren, routers van Ubiquiti UniFi, wifi-versterkers en powerline bridges — alles compatibel met PoE en 10 Gigabit Ethernet.",
    shopTo: "/c/$category",
    shopParams: { category: "netwerken" },
    shopLabel: "Bekijk alle netwerkproducten",
    sections: [
      {
        heading: "Bedraad of draadloos?",
        body:
          "Voor camera's, NVR's, access points en vaste werkplekken kies je altijd bedraad (CAT6a). Voor mobiele apparaten gebruik je wifi 6 access points. Powerline bridges zijn een goede oplossing als nieuw kabelwerk niet mogelijk is.",
      },
      {
        heading: "Waarom CAT6a?",
        body:
          "CAT6a ondersteunt 10 Gigabit tot 100 meter en is volledig backwards compatible. Voor nieuwbouw of renovatie is CAT6a de minimale standaard — futureproof voor jaren vooruit.",
      },
    ],
    benefits: [
      "CAT6a tot 10 Gigabit Ethernet",
      "PoE compatibel voor camera's en AP's",
      "Ubiquiti UniFi: schaalbare WiFi 6",
      "Halogeenvrije kabel (LSZH) leverbaar",
    ],
    applications: [
      "Cameranetwerken (IP/PoE)",
      "Smart home en domotica",
      "Kantoor en thuiswerk",
      "Glasvezel- en bedrijfsnetwerken",
    ],
    faqs: [
      {
        q: "Wat is PoE?",
        a: "Power over Ethernet (PoE) voorziet apparaten zoals IP-camera's, access points en intercoms van zowel data als stroom via één enkele UTP-kabel. Dit bespaart kosten en maakt installatie eenvoudiger omdat je geen aparte stroomvoorziening nodig hebt bij elk apparaat. PoE wordt geleverd via een PoE-switch of injector en is beschikbaar in verschillende standaarden: 802.3af (15W), 802.3at (30W) en 802.3bt (60–90W) voor zwaardere apparaten.",
      },
      {
        q: "Welke router adviseren jullie?",
        a: "Voor professionele omgevingen met camera's, meerdere access points en VLAN-segmentatie adviseren wij Ubiquiti UniFi. UniFi biedt een centraal managementdashboard, seamless roaming tussen access points en uitgebreide beveiligingsopties. Voor eenvoudige thuissituaties volstaat vaak een goede provider-router, maar voor stabiel wifi in grotere woningen of kantoren raden wij een dedicated UniFi-setup aan.",
      },
      {
        q: "Wat is het verschil tussen CAT5e, CAT6 en CAT6a?",
        a: "CAT5e ondersteunt tot 1 Gigabit op 100 meter en volstaat voor de meeste huishoudelijke toepassingen. CAT6 ondersteunt 1 Gigabit op 100 meter en 10 Gigabit tot 55 meter, met betere afscherming tegen storingen. CAT6a ondersteunt 10 Gigabit op de volle 100 meter en heeft een dikkere kabel met superieure afscherming. Voor nieuwbouw en renovaties adviseren wij CAT6a als minimum — dit is futureproof voor jaren.",
      },
      {
        q: "Heb ik een switch nodig en welke?",
        a: "Een switch verdeelt je netwerkverbinding over meerdere apparaten. Heb je meer aansluitingen dan je router biedt, dan heb je een switch nodig. Kies een unmanaged switch voor eenvoudige plug-and-play toepassingen, of een managed switch als je VLAN's, PoE, traffic prioritering (QoS) of beveiliging wilt configureren. Voor camera-installaties met PoE is een managed PoE-switch essentieel.",
      },
      {
        q: "Wat is een mesh wifi-systeem en wanneer heb ik dat nodig?",
        a: "Een mesh-systeem bestaat uit meerdere access points die samen één groot, naadloos wifi-netwerk vormen. Je apparaten schakelen automatisch over naar het dichtstbijzijnde access point zonder handmatig van netwerk te wisselen. Mesh is ideaal voor grote woningen, kantoorpanden met meerdere verdiepingen of gebouwen met dikke muren waar één router onvoldoende bereik geeft.",
      },
      {
        q: "Wat is een VLAN en waarom zou ik dat gebruiken?",
        a: "Een VLAN (Virtual Local Area Network) splitst je fysieke netwerk op in aparte virtuele netwerken. Je kunt bijvoorbeeld je camerasysteem, gastenwifi en bedrijfsnetwerk van elkaar scheiden voor extra veiligheid. Apparaten in verschillende VLAN's kunnen niet direct met elkaar communiceren tenzij je dat expliciet toestaat via firewallregels. Dit beperkt de schade bij een beveiligingsincident aanzienlijk.",
      },
      {
        q: "Wat is het verschil tussen een access point en een wifi-extender?",
        a: "Een access point (AP) maakt directe verbinding met je router of switch via een netwerkkabel en creëert een nieuw wifi-netwerk. Dit geeft de beste prestaties en stabiliteit. Een wifi-extender (repeater) vangt het bestaande wifi-signaal op en herhaalt dit, wat leidt tot halvering van de bandbreedte en meer latency. Voor professioneel gebruik adviseren wij altijd bedrade access points boven draadloze extenders.",
      },
      {
        q: "Hoeveel camera's kan ik op één PoE-switch aansluiten?",
        a: "Dat hangt af van het totale vermogen (wattage) van de switch en het verbruik per camera. Een standaard 8-poorts PoE-switch levert vaak 120W totaal. Een gemiddelde 4MP camera verbruikt 4–6W, dus je kunt er zo'n 20 aansluiten. PTZ-camera's en camera's met verwarming verbruiken meer (15–30W), dus reken hierop bij de planning.",
      },
      {
        q: "Wat is Powerline en wanneer is dat handig?",
        a: "Powerline adapters gebruiken het bestaande elektriciteitsnetwerk in je pand om data te versturen. Je steekt één adapter bij je router in het stopcontact en een andere bij het apparaat dat je wilt aansluiten. Dit is handig als trekken van een UTP-kabel niet praktisch is, bijvoorbeeld door muren of verdiepingen. De snelheid is lager en minder stabiel dan een echte netwerkkabel, maar vaak betrouwbaarder dan wifi op afstand.",
      },
      {
        q: "Welke wifi-standaard heb ik nodig: wifi 5, 6 of 6E?",
        a: "Wifi 5 (802.11ac) volstaat voor de meeste huishoudens met basis internetgebruik. Wifi 6 (802.11ax) biedt hogere snelheden, betere prestaties bij veel gelijktijdige apparaten en lagere latency — ideaal voor smart homes, 4K-streaming en thuiswerken. Wifi 6E voegt het 6 GHz-band toe met nog minder congestie. Voor professionele omgevingen en toekomstbestendige installaties adviseren wij wifi 6 of 6E.",
      },
      {
        q: "Wat is de maximale lengte van een UTP-kabel?",
        a: "De officiële maximumlengte voor een UTP-kabel (CAT5e, CAT6, CAT6a) is 100 meter per segment, gemeten vanaf de switch/router tot het eindapparaat. Dit omvat patchkabels aan beide zijden. Bij grotere afstanden heb je een switch, repeater of glasvezel nodig om het signaal te versterken of te vervangen.",
      },
      {
        q: "Hebben jullie ook halogeenvrije (LSZH) kabel?",
        a: "Ja, wij leveren halogeenvrije kabel met LSZH (Low Smoke Zero Halogen) mantel. Bij brand produceert deze kabel minimale rook en geen corrosieve gassen, wat de veiligheid bij evacuatie verhoogt. LSZH-kabel is verplicht in veel publieke gebouwen, tunnels en gesloten ruimtes, en wordt sterk aanbevolen voor kantoren en professionele installaties.",
      },
    ],
  },
};

export function categoryInfoBySlug(slug: string): CategoryInfo | undefined {
  return CATEGORY_INFO[slug];
}

export const CATEGORY_INFO_SLUGS = Object.keys(CATEGORY_INFO);
